import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import analyticsMlApi from '../services/api/analytics-ml.api';
import type {
  PredictionQuery,
  PredictionRequest,
  AnalyticsEventsLogFilters,
  CreateDataSourceDto,
  UpdateDataSourceDto,
  SystemHealth,
  SystemStats,
  AiInsightsResponse,
  AnalyticsEventEntry,
  DataSourceRecord,
} from '../services/api/analytics-ml.api';
import * as analyticsAnomaliesApi from '../services/api/analytics-anomalies.api';
import analyticsRecommendationsApi from '../services/api/analytics-recommendations.api';
import type {
  RecommendationFilters,
  RecommendationFeedbackPayload,
  ApplyRecommendationPayload,
} from '../services/api/analytics-recommendations.api';
import { logger } from '../utils/logger';
import type {
  IMLModel,
  IPrediction,
  IRecommendationEngine,
  AIRecommendation,
  IAnomalyDetection,
  IAnomalyAlert,
  MLModelStatus,
  IAnomalyResult,
  IAnomalySettings,
  IRecommendationConfig,
  IRecommendationPerformance,
  IRecommendationRule,
  AnomalyAlgorithm,
  AnomalySeverity,
  OutlierStrategy,
  AcknowledgeAllAlertsRequest,
  ResolveAllAlertsRequest,
} from '../types/analytics';
import {
  AnomalyDetectionStatus,
  RecommendationCategory,
  RecommendationPriority,
  RecommendationType,
  RecommendationAlgorithm,
} from '../types/analytics';
import {
  parseDate,
  parseOptionalDate,
  ensureEnumValue,
  normalizeModel,
  mapModelToApi,
} from './analyticsSlice';

// ─── ML-only normalizer constants ─────────────────────────────────────────────

const ANOMALY_ALGORITHM_VALUES = [
  'isolation_forest',
  'one_class_svm',
  'local_outlier_factor',
  'dbscan',
  'statistical',
  'moving_average',
  'seasonal_decomposition',
  'autoencoder',
] as const;

const ANOMALY_SEVERITY_VALUES = ['low', 'medium', 'high', 'critical'] as const;

const ANOMALY_STATUS_VALUES = ['idle', 'running', 'training', 'error', 'paused'] as const;

const OUTLIER_STRATEGY_VALUES = [
  'none',
  'iqr',
  'z_score',
  'isolation_forest',
  'clip',
  'remove',
] as const;

const RECOMMENDATION_CATEGORY_VALUES = Object.values(RecommendationCategory);
const RECOMMENDATION_PRIORITY_VALUES = Object.values(RecommendationPriority);
const RECOMMENDATION_TYPE_VALUES = Object.values(RecommendationType);
const RECOMMENDATION_ALGORITHM_VALUES = Object.values(RecommendationAlgorithm);

// ─── Raw API payload shapes ────────────────────────────────────────────────────

interface RawImpactObject {
  revenue?: number;
  efficiency?: number;
  satisfaction?: number;
  timeframe?: string;
}

interface RawAIRecommendation {
  id?: string;
  recommendationId?: string;
  uid?: string;
  title?: string;
  name?: string;
  description?: string;
  summary?: string;
  category?: string;
  priority?: string;
  confidence?: number;
  score?: number;
  probability?: number;
  impact?: string | RawImpactObject;
  effort?: string;
  expectedImpact?: RawImpactObject;
  timeframe?: string;
  estimatedROI?: number;
  roi?: number;
  implementation?: { steps?: string[]; resources?: string[]; timeline?: string };
  steps?: string[];
  resources?: string[];
  timeline?: string;
}

interface RawRecommendationEngine {
  id?: string;
  engineId?: string;
  name?: string;
  type?: string;
  algorithm?: string;
  config?: Partial<IRecommendationConfig>;
  maxRecommendations?: number;
  minScore?: number;
  diversity?: number;
  freshness?: number;
  popularity?: number;
  personalizedWeight?: number;
  performance?: Partial<IRecommendationPerformance>;
  precision?: number;
  recall?: number;
  coverage?: number;
  novelty?: number;
  clickThroughRate?: number;
  ctr?: number;
  rules?: Array<Partial<IRecommendationRule>>;
}

interface RawPrediction {
  id?: string;
  predictionId?: string;
  modelId?: string;
  model?: { id?: string };
  input?: Record<string, unknown>;
  features?: Record<string, unknown>;
  output?: unknown;
  result?: unknown;
  confidence?: number;
  meta?: { confidence?: number };
  timestamp?: string | Date | number;
  createdAt?: string | Date | number;
  actual?: unknown;
  groundTruth?: unknown;
  error?: number | null;
}

// ─── ML normalizers ────────────────────────────────────────────────────────────

const normalizeAnomalyResult = (result: Partial<IAnomalyResult> | null | undefined): IAnomalyResult => {
  const base: IAnomalyResult = {
    timestamp: parseDate(result?.timestamp),
    value: Number(result?.value ?? 0),
    predicted: Number(result?.predicted ?? 0),
    anomaly: Boolean(result?.anomaly),
    severity: ensureEnumValue(result?.severity, ANOMALY_SEVERITY_VALUES, 'medium') as AnomalySeverity,
    confidence: Number(result?.confidence ?? 0),
  };
  if (result?.explanation !== undefined) {
    base.explanation = result.explanation;
  }
  if (result?.metadata !== undefined) {
    base.metadata = result.metadata;
  }
  return base;
};

const normalizeAnomalyDetection = (detection: Partial<IAnomalyDetection>): IAnomalyDetection => ({
  id: String(detection?.id ?? detection?.widgetId ?? crypto.randomUUID?.() ?? Date.now()),
  widgetId: String(detection?.widgetId ?? ''),
  dataSource: String(detection?.dataSource ?? ''),
  metric: String(detection?.metric ?? ''),
  algorithm: ensureEnumValue(
    detection?.algorithm,
    ANOMALY_ALGORITHM_VALUES,
    'isolation_forest'
  ) as AnomalyAlgorithm,
  settings: {
    algorithm: ensureEnumValue(
      detection?.settings?.algorithm,
      ANOMALY_ALGORITHM_VALUES,
      'isolation_forest'
    ) as AnomalyAlgorithm,
    sensitivity: Number(detection?.settings?.sensitivity ?? 0.5),
    outlierStrategy: ensureEnumValue(
      detection?.settings?.outlierStrategy,
      OUTLIER_STRATEGY_VALUES,
      'iqr'
    ) as OutlierStrategy,
    alertEnabled: Boolean(detection?.settings?.alertEnabled ?? true),
    minSeverity: ensureEnumValue(
      detection?.settings?.minSeverity,
      ANOMALY_SEVERITY_VALUES,
      'medium'
    ) as AnomalySeverity,
    lookbackPeriod: Number(detection?.settings?.lookbackPeriod ?? 30),
    retrainingInterval: Number(detection?.settings?.retrainingInterval ?? 1440),
    parameters: detection?.settings?.parameters || {},
  },
  results: (detection?.results ?? []).map(normalizeAnomalyResult),
  status: ensureEnumValue(
    detection?.status,
    ANOMALY_STATUS_VALUES,
    'idle'
  ) as AnomalyDetectionStatus,
  createdAt: parseDate(detection?.createdAt),
  // lastRunAt и nextRunAt делаем всегда Date, подставляя "нулевую" дату при отсутствии значения
  lastRunAt: parseOptionalDate(detection?.lastRunAt) ?? new Date(0),
  nextRunAt: parseOptionalDate(detection?.nextRunAt) ?? new Date(0),
});

const normalizeAnomalyAlert = (alert: Partial<IAnomalyAlert>): IAnomalyAlert => {
  const base: IAnomalyAlert = {
    id: String(alert?.id ?? crypto.randomUUID?.() ?? Date.now()),
    anomalyId: String(alert?.anomalyId ?? ''),
    severity: ensureEnumValue(alert?.severity, ANOMALY_SEVERITY_VALUES, 'medium') as AnomalySeverity,
    message: String(alert?.message ?? ''),
    timestamp: parseDate(alert?.timestamp),
    acknowledged: Boolean(alert?.acknowledged ?? false),
    resolved: Boolean(alert?.resolved ?? false),
    dismissed: Boolean(alert?.dismissed ?? false),
  };

  const optionalFields: Partial<IAnomalyAlert> = {
    ...(alert?.acknowledgedBy !== undefined && alert.acknowledgedBy !== null
      ? { acknowledgedBy: String(alert.acknowledgedBy) }
      : {}),
    ...(alert?.acknowledgedAt
      ? { acknowledgedAt: parseDate(alert.acknowledgedAt) }
      : {}),
    ...(alert?.resolvedAt ? { resolvedAt: parseDate(alert.resolvedAt) } : {}),
    ...(alert?.dismissedAt ? { dismissedAt: parseDate(alert.dismissedAt) } : {}),
    ...(alert?.dismissalReason !== undefined && alert.dismissalReason !== null
      ? { dismissalReason: String(alert.dismissalReason) }
      : {}),
    ...(alert?.note !== undefined && alert.note !== null
      ? { note: String(alert.note) }
      : {}),
    ...(alert?.resolutionNote !== undefined && alert.resolutionNote !== null
      ? { resolutionNote: String(alert.resolutionNote) }
      : {}),
    ...(alert?.widgetId !== undefined && alert.widgetId !== null
      ? { widgetId: String(alert.widgetId) }
      : {}),
  };

  return { ...base, ...optionalFields };
};

const normalizeAIRecommendation = (recommendation: RawAIRecommendation): AIRecommendation => {
  const id = String(
    recommendation?.id ??
      recommendation?.recommendationId ??
      recommendation?.uid ??
      crypto.randomUUID?.() ??
      Date.now()
  );
  const title = String(recommendation?.title ?? recommendation?.name ?? 'AI Recommendation');
  const description = String(recommendation?.description ?? recommendation?.summary ?? '');

  const category = ensureEnumValue(
    recommendation?.category,
    RECOMMENDATION_CATEGORY_VALUES,
    RecommendationCategory.PERFORMANCE
  ) as RecommendationCategory;

  const priority = ensureEnumValue(
    recommendation?.priority,
    RECOMMENDATION_PRIORITY_VALUES,
    RecommendationPriority.MEDIUM
  ) as RecommendationPriority;

  const confidenceRaw = Number(
    recommendation?.confidence ?? recommendation?.score ?? recommendation?.probability ?? 0.6
  );
  const confidence = Number.isFinite(confidenceRaw)
    ? Math.min(1, Math.max(0, confidenceRaw))
    : 0.6;

  const impactRaw = recommendation?.impact;
  const impactObj = typeof impactRaw === 'object' && impactRaw !== null ? impactRaw : null;

  const impactLevel = ensureEnumValue(
    impactRaw,
    ['low', 'medium', 'high'],
    'medium'
  ) as 'low' | 'medium' | 'high';

  const effortLevel = ensureEnumValue(
    recommendation?.effort,
    ['low', 'medium', 'high'],
    'medium'
  ) as 'low' | 'medium' | 'high';

  const expectedImpact = {
    revenue: Number(recommendation?.expectedImpact?.revenue ?? impactObj?.revenue ?? 0),
    efficiency: Number(recommendation?.expectedImpact?.efficiency ?? impactObj?.efficiency ?? 0),
    satisfaction: Number(recommendation?.expectedImpact?.satisfaction ?? impactObj?.satisfaction ?? 0),
  };

  const timeframe = String(
    recommendation?.timeframe ?? recommendation?.expectedImpact?.timeframe ?? '4 недели'
  );
  const estimatedROI = Number(
    recommendation?.estimatedROI ?? recommendation?.roi ?? expectedImpact.revenue ?? 0
  );
  const steps = recommendation?.implementation?.steps ?? recommendation?.steps ?? [];
  const resources = recommendation?.implementation?.resources ?? recommendation?.resources ?? [];
  const timeline = String(
    recommendation?.implementation?.timeline ?? recommendation?.timeline ?? timeframe
  );

  return {
    id,
    title,
    description,
    category,
    priority,
    impact: impactLevel,
    effort: effortLevel,
    confidence,
    expectedImpact,
    timeframe,
    estimatedROI,
    implementation: {
      steps: steps.map(String),
      resources: resources.map(String),
      timeline,
    },
  };
};

const normalizeRecommendationEngine = (engine: RawRecommendationEngine): IRecommendationEngine => ({
  id: String(engine?.id ?? engine?.engineId ?? crypto.randomUUID?.() ?? Date.now()),
  name: String(engine?.name ?? 'Recommendation Engine'),
  type: ensureEnumValue(
    engine?.type,
    RECOMMENDATION_TYPE_VALUES,
    RecommendationType.HYBRID
  ) as RecommendationType,
  algorithm: ensureEnumValue(
    engine?.algorithm,
    RECOMMENDATION_ALGORITHM_VALUES,
    RecommendationAlgorithm.NEURAL_CF
  ) as RecommendationAlgorithm,
  config: {
    maxRecommendations: Number(engine?.config?.maxRecommendations ?? engine?.maxRecommendations ?? 10),
    minScore: Number(engine?.config?.minScore ?? engine?.minScore ?? 0.4),
    diversity: Number(engine?.config?.diversity ?? engine?.diversity ?? 0.5),
    freshness: Number(engine?.config?.freshness ?? engine?.freshness ?? 0.5),
    popularity: Number(engine?.config?.popularity ?? engine?.popularity ?? 0.5),
    personalizedWeight: Number(
      engine?.config?.personalizedWeight ?? engine?.personalizedWeight ?? 0.5
    ),
  },
  performance: {
    precision: Number(engine?.performance?.precision ?? engine?.precision ?? 0.8),
    recall: Number(engine?.performance?.recall ?? engine?.recall ?? 0.75),
    coverage: Number(engine?.performance?.coverage ?? engine?.coverage ?? 0.6),
    diversity: Number(engine?.performance?.diversity ?? engine?.diversity ?? 0.5),
    novelty: Number(engine?.performance?.novelty ?? engine?.novelty ?? 0.4),
    clickThroughRate: Number(
      engine?.performance?.clickThroughRate ?? engine?.clickThroughRate ?? engine?.ctr ?? 0.1
    ),
  },
  rules: (engine?.rules ?? []).map((rule) => ({
    id: String(rule?.id ?? crypto.randomUUID?.() ?? Date.now()),
    name: String(rule?.name ?? 'Rule'),
    condition: String(rule?.condition ?? ''),
    action: String(rule?.action ?? ''),
    weight: Number(rule?.weight ?? 1),
    enabled: Boolean(rule?.enabled ?? true),
  })),
});

const normalizePrediction = (prediction: RawPrediction): IPrediction => ({
  id: String(prediction?.id ?? prediction?.predictionId ?? crypto.randomUUID?.() ?? Date.now()),
  modelId: String(prediction?.modelId ?? prediction?.model?.id ?? ''),
  input: prediction?.input ?? prediction?.features ?? {},
  output: prediction?.output ?? prediction?.result ?? {},
  confidence: Number(prediction?.confidence ?? prediction?.meta?.confidence ?? 0.5),
  timestamp: parseDate(prediction?.timestamp ?? prediction?.createdAt ?? Date.now()),
  actual: prediction?.actual ?? prediction?.groundTruth,
  // error в IPrediction является обязательным числом, поэтому подставляем 0 при отсутствии значения
  error: prediction?.error !== undefined ? Number(prediction.error) : 0,
});

// ─── ML Thunks ────────────────────────────────────────────────────────────────

export const fetchMLModels = createAsyncThunk(
  'analytics/fetchMLModels',
  async () => {
    try {
      const models = await analyticsMlApi.getMLModels();
      return models.map(normalizeModel);
    } catch (error) {
      logger.error('Не удалось загрузить ML модели аналитики', error);
      throw error;
    }
  }
);

export const fetchRecommendationEngines = createAsyncThunk(
  'analytics/fetchRecommendationEngines',
  async () => {
    try {
      const payload = await analyticsRecommendationsApi.getRecommendationEngines();
      const engines =
        payload?.data?.engines && Array.isArray(payload.data.engines)
          ? payload.data.engines
          : [];
      return engines.map(normalizeRecommendationEngine);
    } catch (error) {
      logger.error('Не удалось загрузить рекомендательные движки', error);
      throw error;
    }
  }
);

export const fetchAiRecommendations = createAsyncThunk(
  'analytics/fetchAiRecommendations',
  async (filters: RecommendationFilters = {}) => {
    try {
      const payload = await analyticsRecommendationsApi.getRecommendations(filters);
      const recommendations =
        payload?.data?.recommendations && Array.isArray(payload.data.recommendations)
          ? payload.data.recommendations
          : [];
      return recommendations.map(normalizeAIRecommendation);
    } catch (error) {
      logger.error('Не удалось загрузить AI рекомендации', error);
      throw error;
    }
  }
);

export const submitRecommendationFeedback = createAsyncThunk(
  'analytics/submitRecommendationFeedback',
  async (
    args: { recommendationId: string } & Pick<
      RecommendationFeedbackPayload,
      'feedback' | 'comment' | 'metadata'
    >
  ) => {
    try {
      const payload: RecommendationFeedbackPayload = {
        feedback: args.feedback,
        ...(args.comment !== undefined ? { comment: args.comment } : {}),
        ...(args.metadata !== undefined ? { metadata: args.metadata } : {}),
      };
      await analyticsRecommendationsApi.sendRecommendationFeedback(
        args.recommendationId,
        payload
      );
      return {
        recommendationId: args.recommendationId,
        feedback: args.feedback,
      };
    } catch (error) {
      logger.error('Не удалось отправить обратную связь по рекомендации', error);
      throw error;
    }
  }
);

export const applyAiRecommendation = createAsyncThunk(
  'analytics/applyAiRecommendation',
  async (args: { recommendationId: string } & ApplyRecommendationPayload) => {
    try {
      const payload: ApplyRecommendationPayload = {
        ...(args.context !== undefined ? { context: args.context } : {}),
        ...(args.parameters !== undefined ? { parameters: args.parameters } : {}),
      };
      const response = await analyticsRecommendationsApi.applyRecommendation(
        args.recommendationId,
        payload
      );
      const responsePayload = response;
      const success = Boolean(responsePayload?.data?.success ?? true);
      const jobId = responsePayload?.data?.jobId;
      return {
        recommendationId: args.recommendationId,
        success,
        jobId: jobId ? String(jobId) : undefined,
      };
    } catch (error) {
      logger.error('Не удалось применить AI рекомендацию', error);
      throw error;
    }
  }
);

export const fetchPredictions = createAsyncThunk(
  'analytics/fetchPredictions',
  async (query: PredictionQuery = {}) => {
    try {
      const predictions = await analyticsMlApi.getPredictions(query);
      const predictionsArray = Array.isArray(predictions) ? predictions : [];
      return predictionsArray.map(normalizePrediction);
    } catch (error) {
      logger.error('Не удалось загрузить прогнозы', error);
      throw error;
    }
  }
);

export const runPrediction = createAsyncThunk(
  'analytics/runPrediction',
  async (request: PredictionRequest) => {
    try {
      const response = await analyticsMlApi.generatePrediction(request);
      const predictionData =
        (response as unknown as { prediction?: IPrediction })?.prediction ?? response;
      return normalizePrediction(predictionData);
    } catch (error) {
      logger.error('Не удалось выполнить прогнозирование', error);
      throw error;
    }
  }
);

export const trainMLModel = createAsyncThunk(
  'analytics/trainMLModel',
  async (modelConfig: Partial<IMLModel>) => {
    try {
      const apiModelConfig = mapModelToApi(modelConfig);
      const createdModel = await analyticsMlApi.createMLModel(apiModelConfig);
      let latestModel = createdModel;
      if (createdModel?.id) {
        try {
          await analyticsMlApi.trainMLModel(createdModel.id);
          latestModel = await analyticsMlApi.getMLModel(createdModel.id);
        } catch (trainError) {
          logger.warn('Обучение ML модели завершилось с предупреждением', trainError);
        }
      }
      return normalizeModel(latestModel);
    } catch (error) {
      logger.error('Не удалось обучить ML модель', error);
      throw error;
    }
  }
);

export const deployMLModel = createAsyncThunk(
  'analytics/deployMLModel',
  async (modelId: string) => {
    try {
      await analyticsMlApi.deployMLModel(modelId);
      const updatedModel = await analyticsMlApi.getMLModel(modelId);
      return normalizeModel(updatedModel);
    } catch (error) {
      logger.error('Не удалось развернуть ML модель', error);
      throw error;
    }
  }
);

export const detectAnomalies = createAsyncThunk(
  'analytics/detectAnomalies',
  async (params: {
    widgetId: string;
    dataSource: string;
    metric: string;
    settings: IAnomalySettings;
  }) => {
    try {
      const detection = await analyticsAnomaliesApi.detectAnomalies(params);
      return normalizeAnomalyDetection(detection);
    } catch (error) {
      logger.error('Не удалось выполнить обнаружение аномалий', error);
      throw error;
    }
  }
);

export const trainAnomalyModel = createAsyncThunk(
  'analytics/trainAnomalyModel',
  async (params: {
    widgetId: string;
    dataSource: string;
    metric: string;
    algorithm: AnomalyAlgorithm;
    parameters: Record<string, unknown>;
  }) => {
    try {
      const response = await analyticsAnomaliesApi.trainAnomalyModel(params);
      return {
        widgetId: response.widgetId,
        algorithm: response.algorithm,
        accuracy: response.accuracy,
        trainedAt: parseDate(response.trainedAt),
      };
    } catch (error) {
      logger.error('Не удалось обучить модель обнаружения аномалий', error);
      throw error;
    }
  }
);

export const updateAnomalySettings = createAsyncThunk(
  'analytics/updateAnomalySettings',
  async (params: { widgetId: string; settings: IAnomalySettings }) => {
    try {
      const detection = await analyticsAnomaliesApi.updateAnomalySettings(params);
      return normalizeAnomalyDetection(detection);
    } catch (error) {
      logger.error('Не удалось обновить настройки обнаружения аномалий', error);
      throw error;
    }
  }
);

export const getAnomalyAlerts = createAsyncThunk(
  'analytics/getAnomalyAlerts',
  async (widgetId?: string) => {
    try {
      const alerts = await analyticsAnomaliesApi.getAnomalyAlerts(
        widgetId ? { widgetId } : {}
      );
      return alerts.map(normalizeAnomalyAlert);
    } catch (error) {
      logger.error('Не удалось получить алерты аномалий', error);
      throw error;
    }
  }
);

export const acknowledgeAnomalyAlert = createAsyncThunk(
  'analytics/acknowledgeAnomalyAlert',
  async (params: { alertId: string; note?: string }, { dispatch }) => {
    try {
      const updatedAlert = await analyticsAnomaliesApi.acknowledgeAnomalyAlert(
        params.alertId,
        params.note ? { note: params.note } : undefined
      );
      const normalized = normalizeAnomalyAlert(updatedAlert);
      if (normalized.widgetId) {
        dispatch(getAnomalyAlerts(normalized.widgetId));
      }
      return normalized;
    } catch (error) {
      logger.error('Не удалось подтвердить алерт', error);
      throw error;
    }
  }
);

export const resolveAnomalyAlert = createAsyncThunk(
  'analytics/resolveAnomalyAlert',
  async (params: { alertId: string; resolutionNote?: string }, { dispatch }) => {
    try {
      const updatedAlert = await analyticsAnomaliesApi.resolveAnomalyAlert(
        params.alertId,
        params.resolutionNote ? { resolutionNote: params.resolutionNote } : undefined
      );
      const normalized = normalizeAnomalyAlert(updatedAlert);
      if (normalized.widgetId) {
        dispatch(getAnomalyAlerts(normalized.widgetId));
      }
      return normalized;
    } catch (error) {
      logger.error('Не удалось разрешить алерт', error);
      throw error;
    }
  }
);

export const dismissAnomalyAlert = createAsyncThunk(
  'analytics/dismissAnomalyAlert',
  async (params: { alertId: string; reason?: string }, { dispatch }) => {
    try {
      const updatedAlert = await analyticsAnomaliesApi.dismissAnomalyAlert(
        params.alertId,
        params.reason ? { reason: params.reason } : undefined
      );
      const normalized = normalizeAnomalyAlert(updatedAlert);
      if (normalized.widgetId) {
        dispatch(getAnomalyAlerts(normalized.widgetId));
      }
      return normalized;
    } catch (error) {
      logger.error('Не удалось отклонить алерт', error);
      throw error;
    }
  }
);

export const acknowledgeAllAlerts = createAsyncThunk(
  'analytics/acknowledgeAllAlerts',
  async (
    params: {
      widgetId?: string;
      severity?: AnomalySeverity;
      filter?: AcknowledgeAllAlertsRequest['filter'];
    },
    { dispatch }
  ) => {
    try {
      const request: AcknowledgeAllAlertsRequest = {
        ...(params.widgetId !== undefined ? { widgetId: params.widgetId } : {}),
        ...(params.severity !== undefined ? { severity: params.severity } : {}),
        ...(params.filter !== undefined ? { filter: params.filter } : {}),
      };
      const response = await analyticsAnomaliesApi.acknowledgeAllAlerts(request);
      if (params.widgetId) {
        dispatch(getAnomalyAlerts(params.widgetId));
      }
      return response;
    } catch (error) {
      logger.error('Не удалось подтвердить все алерты', error);
      throw error;
    }
  }
);

export const resolveAllAlerts = createAsyncThunk(
  'analytics/resolveAllAlerts',
  async (
    params: {
      widgetId?: string;
      severity?: AnomalySeverity;
      filter?: ResolveAllAlertsRequest['filter'];
    },
    { dispatch }
  ) => {
    try {
      const request: ResolveAllAlertsRequest = {
        ...(params.widgetId !== undefined ? { widgetId: params.widgetId } : {}),
        ...(params.severity !== undefined ? { severity: params.severity } : {}),
        ...(params.filter !== undefined ? { filter: params.filter } : {}),
      };
      const response = await analyticsAnomaliesApi.resolveAllAlerts(request);
      if (params.widgetId) {
        dispatch(getAnomalyAlerts(params.widgetId));
      }
      return response;
    } catch (error) {
      logger.error('Не удалось разрешить все алерты', error);
      throw error;
    }
  }
);

export const getCorrelations = createAsyncThunk(
  'analytics/getCorrelations',
  async (params: {
    widgetId: string;
    primaryMetric: string;
    secondaryMetric: string;
    dateRange?: { start: string; end: string };
    limit?: number;
  }) => {
    try {
      const correlations = await analyticsAnomaliesApi.getCorrelations(params);
      return correlations;
    } catch (error) {
      logger.error('Не удалось получить корреляции', error);
      throw error;
    }
  }
);

export const fetchDataSources = createAsyncThunk(
  'analytics/fetchDataSources',
  async (params?: Parameters<typeof analyticsMlApi.getDataSources>[0]) => {
    try {
      return await analyticsMlApi.getDataSources(params);
    } catch (error) {
      logger.error('Не удалось загрузить источники данных', error);
      throw error;
    }
  }
);

export const createDataSourceAsync = createAsyncThunk(
  'analytics/createDataSource',
  async (dto: CreateDataSourceDto) => {
    try {
      return await analyticsMlApi.createDataSource(dto);
    } catch (error) {
      logger.error('Не удалось создать источник данных', error);
      throw error;
    }
  }
);

export const updateDataSourceAsync = createAsyncThunk(
  'analytics/updateDataSource',
  async ({ id, dto }: { id: string; dto: UpdateDataSourceDto }) => {
    try {
      return await analyticsMlApi.updateDataSource(id, dto);
    } catch (error) {
      logger.error('Не удалось обновить источник данных', error);
      throw error;
    }
  }
);

export const deleteDataSourceAsync = createAsyncThunk(
  'analytics/deleteDataSource',
  async (id: string) => {
    try {
      await analyticsMlApi.deleteDataSource(id);
      return id;
    } catch (error) {
      logger.error('Не удалось удалить источник данных', error);
      throw error;
    }
  }
);

export const fetchSystemHealth = createAsyncThunk(
  'analytics/fetchSystemHealth',
  async () => {
    try {
      return await analyticsMlApi.getSystemHealth();
    } catch (error) {
      logger.error('Не удалось загрузить состояние системы', error);
      throw error;
    }
  }
);

export const fetchSystemStats = createAsyncThunk(
  'analytics/fetchSystemStats',
  async () => {
    try {
      return await analyticsMlApi.getSystemStats();
    } catch (error) {
      logger.error('Не удалось загрузить статистику системы', error);
      throw error;
    }
  }
);

export const fetchAiInsights = createAsyncThunk(
  'analytics/fetchAiInsights',
  async () => {
    try {
      return await analyticsMlApi.getInsights();
    } catch (error) {
      logger.error('Не удалось загрузить AI-инсайты', error);
      throw error;
    }
  }
);

export const fetchAnalyticsEventsLog = createAsyncThunk(
  'analytics/fetchAnalyticsEventsLog',
  async (filters: AnalyticsEventsLogFilters = {}) => {
    try {
      return await analyticsMlApi.getAnalyticsEventsLog(filters);
    } catch (error) {
      logger.error('Не удалось загрузить журнал событий', error);
      throw error;
    }
  }
);

// ─── State ────────────────────────────────────────────────────────────────────

interface MlAnalyticsState {
  // Infrastructure
  dataSourceRecords: DataSourceRecord[];
  systemHealth: SystemHealth | null;
  systemStats: SystemStats | null;
  aiInsights: AiInsightsResponse | null;
  analyticsEventsLog: { events: AnalyticsEventEntry[]; total: number };

  // ML models
  mlModels: IMLModel[];
  activeModel: IMLModel | null;

  // Predictions
  predictions: IPrediction[];

  // Recommendations
  recommendationEngines: IRecommendationEngine[];
  recommendations: AIRecommendation[];
  recommendationFeedback: Record<string, 'positive' | 'negative'>;
  appliedRecommendations: Record<string, { appliedAt: string; jobId?: string }>;

  // Anomaly detection
  anomalyDetections: IAnomalyDetection[];
  anomalyAlerts: IAnomalyAlert[];
  activeAnomalyDetection: IAnomalyDetection | null;

  loading: {
    dataSources: boolean;
    systemHealth: boolean;
    insights: boolean;
    eventsLog: boolean;
    models: boolean;
    predictions: boolean;
    recommendations: boolean;
    anomalies: boolean;
  };
  error: string | null;
}

const initialState: MlAnalyticsState = {
  dataSourceRecords: [],
  systemHealth: null,
  systemStats: null,
  aiInsights: null,
  analyticsEventsLog: { events: [], total: 0 },

  mlModels: [],
  activeModel: null,

  predictions: [],

  recommendationEngines: [],
  recommendations: [],
  recommendationFeedback: {},
  appliedRecommendations: {},

  anomalyDetections: [],
  anomalyAlerts: [],
  activeAnomalyDetection: null,

  loading: {
    dataSources: false,
    systemHealth: false,
    insights: false,
    eventsLog: false,
    models: false,
    predictions: false,
    recommendations: false,
    anomalies: false,
  },
  error: null,
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const mlAnalyticsSlice = createSlice({
  name: 'mlAnalytics',
  initialState,
  reducers: {
    clearMlError: (state) => {
      state.error = null;
    },
    setActiveModel: (state, action: PayloadAction<IMLModel | null>) => {
      state.activeModel = action.payload;
    },
    updateModelStatus: (state, action: PayloadAction<{ id: string; status: MLModelStatus }>) => {
      const model = state.mlModels.find((m) => m.id === action.payload.id);
      if (model) {
        model.status = action.payload.status;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ── Data Sources ────────────────────────────────────────────────────────
      .addCase(fetchDataSources.pending, (state) => {
        state.loading.dataSources = true;
        state.error = null;
      })
      .addCase(fetchDataSources.fulfilled, (state, action) => {
        state.loading.dataSources = false;
        state.dataSourceRecords = action.payload;
      })
      .addCase(fetchDataSources.rejected, (state, action) => {
        state.loading.dataSources = false;
        state.error = action.error.message ?? 'Ошибка загрузки источников данных';
      })

      .addCase(createDataSourceAsync.pending, (state) => {
        state.loading.dataSources = true;
      })
      .addCase(createDataSourceAsync.fulfilled, (state, action) => {
        state.loading.dataSources = false;
        state.dataSourceRecords.push(action.payload);
      })
      .addCase(createDataSourceAsync.rejected, (state, action) => {
        state.loading.dataSources = false;
        state.error = action.error.message ?? 'Ошибка создания источника данных';
      })

      .addCase(updateDataSourceAsync.pending, (state) => {
        state.loading.dataSources = true;
      })
      .addCase(updateDataSourceAsync.fulfilled, (state, action) => {
        state.loading.dataSources = false;
        const index = state.dataSourceRecords.findIndex((ds) => ds.id === action.payload.id);
        if (index >= 0) state.dataSourceRecords[index] = action.payload;
      })
      .addCase(updateDataSourceAsync.rejected, (state, action) => {
        state.loading.dataSources = false;
        state.error = action.error.message ?? 'Ошибка обновления источника данных';
      })

      .addCase(deleteDataSourceAsync.pending, (state) => {
        state.loading.dataSources = true;
      })
      .addCase(deleteDataSourceAsync.fulfilled, (state, action) => {
        state.loading.dataSources = false;
        state.dataSourceRecords = state.dataSourceRecords.filter((ds) => ds.id !== action.payload);
      })
      .addCase(deleteDataSourceAsync.rejected, (state, action) => {
        state.loading.dataSources = false;
        state.error = action.error.message ?? 'Ошибка удаления источника данных';
      })

      // ── System Health ───────────────────────────────────────────────────────
      .addCase(fetchSystemHealth.pending, (state) => {
        state.loading.systemHealth = true;
        state.error = null;
      })
      .addCase(fetchSystemHealth.fulfilled, (state, action) => {
        state.loading.systemHealth = false;
        state.systemHealth = action.payload;
      })
      .addCase(fetchSystemHealth.rejected, (state, action) => {
        state.loading.systemHealth = false;
        state.error = action.error.message ?? 'Ошибка загрузки состояния системы';
      })

      .addCase(fetchSystemStats.pending, (state) => {
        state.loading.systemHealth = true;
      })
      .addCase(fetchSystemStats.fulfilled, (state, action) => {
        state.loading.systemHealth = false;
        state.systemStats = action.payload;
      })
      .addCase(fetchSystemStats.rejected, (state, action) => {
        state.loading.systemHealth = false;
        state.error = action.error.message ?? 'Ошибка загрузки статистики системы';
      })

      // ── AI Insights ─────────────────────────────────────────────────────────
      .addCase(fetchAiInsights.pending, (state) => {
        state.loading.insights = true;
        state.error = null;
      })
      .addCase(fetchAiInsights.fulfilled, (state, action) => {
        state.loading.insights = false;
        state.aiInsights = action.payload;
      })
      .addCase(fetchAiInsights.rejected, (state, action) => {
        state.loading.insights = false;
        state.error = action.error.message ?? 'Ошибка загрузки AI-инсайтов';
      })

      // ── Analytics Events Log ────────────────────────────────────────────────
      .addCase(fetchAnalyticsEventsLog.pending, (state) => {
        state.loading.eventsLog = true;
        state.error = null;
      })
      .addCase(fetchAnalyticsEventsLog.fulfilled, (state, action) => {
        state.loading.eventsLog = false;
        state.analyticsEventsLog = action.payload;
      })
      .addCase(fetchAnalyticsEventsLog.rejected, (state, action) => {
        state.loading.eventsLog = false;
        state.error = action.error.message ?? 'Ошибка загрузки журнала событий';
      })

      // ── ML Models ───────────────────────────────────────────────────────────
      .addCase(fetchMLModels.pending, (state) => {
        state.loading.models = true;
      })
      .addCase(fetchMLModels.fulfilled, (state, action) => {
        state.loading.models = false;
        state.mlModels = action.payload;
      })
      .addCase(fetchMLModels.rejected, (state, action) => {
        state.loading.models = false;
        state.error = action.error.message ?? 'Ошибка загрузки моделей';
      })

      .addCase(trainMLModel.pending, (state) => {
        state.loading.models = true;
      })
      .addCase(trainMLModel.fulfilled, (state, action) => {
        state.loading.models = false;
        const index = state.mlModels.findIndex((m) => m.id === action.payload.id);
        if (index >= 0) {
          state.mlModels[index] = action.payload;
        } else {
          state.mlModels.push(action.payload);
        }
      })
      .addCase(trainMLModel.rejected, (state, action) => {
        state.loading.models = false;
        state.error = action.error.message ?? 'Ошибка обучения модели';
      })

      .addCase(deployMLModel.pending, (state) => {
        state.loading.models = true;
      })
      .addCase(deployMLModel.fulfilled, (state, action) => {
        state.loading.models = false;
        const index = state.mlModels.findIndex((m) => m.id === action.payload.id);
        if (index >= 0) {
          state.mlModels[index] = action.payload;
        } else {
          state.mlModels.push(action.payload);
        }
      })
      .addCase(deployMLModel.rejected, (state, action) => {
        state.loading.models = false;
        state.error = action.error.message ?? 'Ошибка развертывания модели';
      })

      // ── Predictions ─────────────────────────────────────────────────────────
      .addCase(fetchPredictions.pending, (state) => {
        state.loading.predictions = true;
      })
      .addCase(fetchPredictions.fulfilled, (state, action) => {
        state.loading.predictions = false;
        state.predictions = action.payload;
      })
      .addCase(fetchPredictions.rejected, (state, action) => {
        state.loading.predictions = false;
        state.error = action.error.message ?? 'Ошибка загрузки прогнозов';
      })

      .addCase(runPrediction.pending, (state) => {
        state.loading.predictions = true;
      })
      .addCase(runPrediction.fulfilled, (state, action) => {
        state.loading.predictions = false;
        state.predictions = [action.payload, ...state.predictions].slice(0, 100);
      })
      .addCase(runPrediction.rejected, (state, action) => {
        state.loading.predictions = false;
        state.error = action.error.message ?? 'Ошибка выполнения прогноза';
      })

      // ── Recommendations ─────────────────────────────────────────────────────
      .addCase(fetchRecommendationEngines.pending, (state) => {
        state.loading.recommendations = true;
      })
      .addCase(fetchRecommendationEngines.fulfilled, (state, action) => {
        state.loading.recommendations = false;
        state.recommendationEngines = action.payload;
      })
      .addCase(fetchRecommendationEngines.rejected, (state, action) => {
        state.loading.recommendations = false;
        state.error = action.error.message ?? 'Ошибка загрузки рекомендательных движков';
      })

      .addCase(fetchAiRecommendations.pending, (state) => {
        state.loading.recommendations = true;
        state.error = null;
      })
      .addCase(fetchAiRecommendations.fulfilled, (state, action) => {
        state.loading.recommendations = false;
        state.recommendations = action.payload;
      })
      .addCase(fetchAiRecommendations.rejected, (state, action) => {
        state.loading.recommendations = false;
        state.error = action.error.message ?? 'Ошибка загрузки AI рекомендаций';
      })

      .addCase(submitRecommendationFeedback.fulfilled, (state, action) => {
        state.recommendationFeedback[action.payload.recommendationId] = action.payload.feedback;
      })
      .addCase(submitRecommendationFeedback.rejected, (state, action) => {
        state.error = action.error.message ?? 'Ошибка отправки обратной связи по рекомендации';
      })

      .addCase(applyAiRecommendation.fulfilled, (state, action) => {
        state.appliedRecommendations[action.payload.recommendationId] = {
          appliedAt: new Date().toISOString(),
          ...(action.payload.jobId ? { jobId: action.payload.jobId } : {}),
        };
      })
      .addCase(applyAiRecommendation.rejected, (state, action) => {
        state.error = action.error.message ?? 'Ошибка применения рекомендации';
      })

      // ── Anomaly Detection ───────────────────────────────────────────────────
      .addCase(detectAnomalies.pending, (state) => {
        state.loading.anomalies = true;
      })
      .addCase(detectAnomalies.fulfilled, (state, action) => {
        state.loading.anomalies = false;
        const idx = state.anomalyDetections.findIndex((d) => d.id === action.payload.id);
        if (idx >= 0) {
          state.anomalyDetections[idx] = action.payload;
        } else {
          state.anomalyDetections.push(action.payload);
        }
        state.activeAnomalyDetection = action.payload;
      })
      .addCase(detectAnomalies.rejected, (state, action) => {
        state.loading.anomalies = false;
        state.error = action.error.message ?? 'Ошибка обнаружения аномалий';
      })

      .addCase(trainAnomalyModel.pending, (state) => {
        state.loading.models = true;
      })
      .addCase(trainAnomalyModel.fulfilled, (state, action) => {
        state.loading.models = false;
        const detection = state.anomalyDetections.find(
          (d) => d.widgetId === action.payload.widgetId,
        );
        if (detection) {
          detection.algorithm = action.payload.algorithm;
          detection.lastRunAt = action.payload.trainedAt;
          detection.status = AnomalyDetectionStatus.IDLE;
        }
      })
      .addCase(trainAnomalyModel.rejected, (state, action) => {
        state.loading.models = false;
        state.error = action.error.message ?? 'Ошибка обучения модели аномалий';
      })

      .addCase(updateAnomalySettings.pending, (state) => {
        state.loading.models = true;
      })
      .addCase(updateAnomalySettings.fulfilled, (state, action) => {
        state.loading.models = false;
        const idx = state.anomalyDetections.findIndex((d) => d.id === action.payload.id);
        if (idx >= 0) {
          state.anomalyDetections[idx] = action.payload;
        } else {
          state.anomalyDetections.push(action.payload);
        }
        if (state.activeAnomalyDetection?.id === action.payload.id) {
          state.activeAnomalyDetection = action.payload;
        }
      })
      .addCase(updateAnomalySettings.rejected, (state, action) => {
        state.loading.models = false;
        state.error = action.error.message ?? 'Ошибка обновления настроек аномалий';
      })

      .addCase(getAnomalyAlerts.pending, (state) => {
        state.loading.anomalies = true;
      })
      .addCase(getAnomalyAlerts.fulfilled, (state, action) => {
        state.loading.anomalies = false;
        state.anomalyAlerts = action.payload;
      })
      .addCase(getAnomalyAlerts.rejected, (state, action) => {
        state.loading.anomalies = false;
        state.error = action.error.message ?? 'Ошибка получения алертов';
      })

      .addCase(acknowledgeAnomalyAlert.pending, (state) => {
        state.loading.anomalies = true;
      })
      .addCase(acknowledgeAnomalyAlert.fulfilled, (state, action) => {
        state.loading.anomalies = false;
        const idx = state.anomalyAlerts.findIndex((a) => a.id === action.payload.id);
        if (idx !== -1) state.anomalyAlerts[idx] = action.payload;
      })
      .addCase(acknowledgeAnomalyAlert.rejected, (state, action) => {
        state.loading.anomalies = false;
        state.error = action.error.message ?? 'Ошибка подтверждения алерта';
      })

      .addCase(resolveAnomalyAlert.pending, (state) => {
        state.loading.anomalies = true;
      })
      .addCase(resolveAnomalyAlert.fulfilled, (state, action) => {
        state.loading.anomalies = false;
        const idx = state.anomalyAlerts.findIndex((a) => a.id === action.payload.id);
        if (idx !== -1) state.anomalyAlerts[idx] = action.payload;
      })
      .addCase(resolveAnomalyAlert.rejected, (state, action) => {
        state.loading.anomalies = false;
        state.error = action.error.message ?? 'Ошибка разрешения алерта';
      })

      .addCase(dismissAnomalyAlert.pending, (state) => {
        state.loading.anomalies = true;
      })
      .addCase(dismissAnomalyAlert.fulfilled, (state, action) => {
        state.loading.anomalies = false;
        const idx = state.anomalyAlerts.findIndex((a) => a.id === action.payload.id);
        if (idx !== -1) state.anomalyAlerts[idx] = action.payload;
      })
      .addCase(dismissAnomalyAlert.rejected, (state, action) => {
        state.loading.anomalies = false;
        state.error = action.error.message ?? 'Ошибка отклонения алерта';
      })

      .addCase(acknowledgeAllAlerts.pending, (state) => {
        state.loading.anomalies = true;
      })
      .addCase(acknowledgeAllAlerts.fulfilled, (state) => {
        state.loading.anomalies = false;
      })
      .addCase(acknowledgeAllAlerts.rejected, (state, action) => {
        state.loading.anomalies = false;
        state.error = action.error.message ?? 'Ошибка массового подтверждения алертов';
      })

      .addCase(resolveAllAlerts.pending, (state) => {
        state.loading.anomalies = true;
      })
      .addCase(resolveAllAlerts.fulfilled, (state) => {
        state.loading.anomalies = false;
      })
      .addCase(resolveAllAlerts.rejected, (state, action) => {
        state.loading.anomalies = false;
        state.error = action.error.message ?? 'Ошибка массового разрешения алертов';
      });
  },
});

export const { clearMlError, setActiveModel, updateModelStatus } = mlAnalyticsSlice.actions;
export default mlAnalyticsSlice.reducer;
